import MapView from "@arcgis/core/views/MapView";
import GeoJSONLayer from "@arcgis/core/layers/GeoJSONLayer";
import { whenOnce } from "@arcgis/core/core/reactiveUtils";
import Point from "@arcgis/core/geometry/Point";
import {
  getMarkerPopupHorizontalPlacement,
  getMarkerPopupPlacement,
} from "@components/world-map/world-map-utils";
import {
  MarkerPopupHorizontalPlacement,
  MarkerPopupPlacement,
} from "@components/world-map/world-map-types";

/** Vertical and horizontal popup placement relative to a marker. */
export interface PopupPlacementInfo {
  /** Vertical popup placement relative to marker. */
  placement: MarkerPopupPlacement;

  /** Horizontal popup placement relative to marker. */
  horizontalPlacement: MarkerPopupHorizontalPlacement;
}

/** Amount to increase zoom when clicking into a cluster. */
const CLUSTER_ZOOM_INCREASE = 3;

/**
 * Computes vertical and horizontal popup placement for a marker
 * given its screen position and the map view dimensions.
 */
export function getPopupPlacement(
  screenPoint: __esri.ScreenPoint,
  viewWidth: number,
  viewHeight: number
): PopupPlacementInfo {
  const placement = getMarkerPopupPlacement(screenPoint.y, viewHeight);

  if (placement === "center") {
    return {
      placement,
      horizontalPlacement: screenPoint.x <= viewWidth / 2 ? "right" : "left",
    };
  }

  return {
    placement,
    horizontalPlacement: getMarkerPopupHorizontalPlacement(
      screenPoint.x,
      viewWidth
    ),
  };
}

/**
 * Hit-tests the map at the given event position and returns the first layer graphic,
 * regardless of whether it is a cluster or an individual marker.
 */
export async function getAnyLayerGraphic(
  view: MapView,
  layer: GeoJSONLayer | null,
  event:
    | __esri.ViewClickEvent
    | __esri.ViewPointerMoveEvent
    | __esri.ScreenPoint
): Promise<__esri.Graphic | null> {
  const hitTest = layer
    ? await view.hitTest(event, { include: [layer] })
    : await view.hitTest(event);

  const graphicResult = hitTest.results.find((r) => r.type === "graphic");
  return graphicResult?.type === "graphic" ? graphicResult.graphic : null;
}

/**
 * Zooms into a cluster graphic center to reveal individual markers.
 * Waits for the view to settle, then refreshes the layer to clear cached cluster graphics.
 */
export async function zoomIntoCluster(
  view: MapView,
  layer: GeoJSONLayer,
  clusterGraphic: __esri.Graphic
): Promise<void> {
  await view.goTo({
    center: clusterGraphic.geometry as Point,
    zoom: view.zoom + CLUSTER_ZOOM_INCREASE,
  });

  // Wait until the view is fully settled after the zoom animation.
  if (!view.stationary) {
    await whenOnce(() => view.stationary);
  }

  // Force the layer to re-fetch its data and recalculate clustering from scratch.
  // Without this, ArcGIS sometimes caches the old cluster graphics and doesn't
  // re-evaluate them after a programmatic goTo.
  layer.refresh();
}
